import React, { useState, useEffect } from 'react';
import { auth } from '../utils/firebase';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, orderBy, query, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { db, storage } from '../utils/firebase';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { formatDateForDisplay } from '../utils/dateUtils';
import { onAuthStateChanged, signOut } from 'firebase/auth';

interface Activity {
  id: string;
  title: string;
  date: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin');
      }
    });

    return () => {
      unsubAuth();
    };
  }, [navigate]);

  useEffect(() => {
    const fetchActivities = async () => {
      const activitiesCollection = collection(db, 'activities');
      const q = query(activitiesCollection, orderBy('date', 'desc'));
      const querySnapshot = await getDocs(q);
      const activitiesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().title,
        date: doc.data().date,
      } as Activity));
      setActivities(activitiesList);
    };

    fetchActivities();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } finally {
      navigate('/admin');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('本当に削除しますか？')) return;
    try {
      // 画像パスがあれば先に削除
      const targetRef = doc(db, 'activities', id);
      const snap = await getDoc(targetRef);
      if (snap.exists()) {
        const data: any = snap.data();
        if (data?.imageUrl) {
          try {
            await deleteObject(storageRef(storage, data.imageUrl));
          } catch (err) {
            // 画像が存在しない等は無視して続行
            console.warn('image delete skipped:', err);
          }
        }
      }
      await deleteDoc(targetRef);
      setActivities((prev) => prev.filter((a) => a.id !== id));
      alert('削除しました');
    } catch (err) {
      console.error('Failed to delete activity:', err);
      alert('削除に失敗しました。時間をおいて再度お試しください。');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8" style={{ paddingTop: '80px' }}>
      <div className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-4">管理者ダッシュボード</h1>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/admin/activity/new')}
              className="bg-blue-400 hover:bg-blue-500 text-white font-bold py-2 px-4 rounded"
            >
              新規作成
            </button>
            <button
              onClick={handleLogout}
              className="bg-red-400 hover:bg-red-500 text-white font-bold py-2 px-4 rounded"
            >
              ログアウト
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">タイトル</th>
                <th className="py-2 px-4 border-b">日付</th>
                <th className="py-2 px-4 border-b">操作</th>
              </tr>
            </thead>
            <tbody>
              {activities.map(activity => (
                <tr key={activity.id}>
                  <td className="py-2 px-4 border-b">{activity.title}</td>
                  <td className="py-2 px-4 border-b">{formatDateForDisplay(new Date(activity.date))}</td>
                  <td className="py-2 px-4 border-b">
                    <button
                      onClick={() => navigate(`/admin/activity/edit/${activity.id}`)}
                      className="bg-green-400 hover:bg-green-500 text-white font-bold py-1 px-2 rounded mr-2"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDelete(activity.id)}
                      className="bg-red-400 hover:bg-red-500 text-white font-bold py-1 px-2 rounded"
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
